import { useEffect, useState } from 'react';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { GetWorkerQuery } from '@/types/graphql';
import {
  OnboardingStepsAPI,
  OnboardingStep,
} from '@/services/onboardingStepsAPI';

type Props = {
  worker: GetWorkerQuery['worker'];
  currentAdminIsCustomerAdmin: boolean;
};

const stepStatusColor = {
  pending: '#6B7280',
  in_progress: '#F59E0B',
  completed: '#10B981',
  under_review: '#3B82F6',
  rejected: '#EF4444',
};

const stepStatusLabel = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  under_review: 'Under Review',
  rejected: 'Rejected',
};

const OnboardingStepsCard = ({
  worker,
  currentAdminIsCustomerAdmin,
}: Props) => {
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  const fetchOnboardingSteps = async () => {
    try {
      setLoading(true);
      const response = await OnboardingStepsAPI.getWorkerOnboardingSteps(
        parseInt(worker.id),
      );
      if (response.success && response.steps) {
        setSteps(response.steps);
      }
    } catch (err) {
      setError('Failed to load onboarding steps');
      console.error('Error fetching onboarding steps:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnboardingSteps();
  }, [worker.id]);

  const handleStatusChange = async (stepId: string, newStatus: string) => {
    try {
      setUpdating(stepId);
      const response = await OnboardingStepsAPI.updateWorkerOnboardingSteps(
        parseInt(worker.id),
        stepId,
        newStatus,
      );

      if (response.success && response.steps) {
        setSteps(response.steps);
      }
    } catch (err) {
      console.error('Error updating step status:', err);
      setError('Failed to update step status');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <Card title="Onboarding Steps">
        <Card.Section>
          <Body>Loading...</Body>
        </Card.Section>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Onboarding Steps">
        <Card.Section>
          <Body color="lighter">{error}</Body>
        </Card.Section>
      </Card>
    );
  }

  if (!steps.length) {
    return (
      <Card title="Onboarding Steps">
        <Card.Section>
          <Body color="lighter">Onboarding data is missing</Body>
        </Card.Section>
      </Card>
    );
  }

  const completedSteps = steps.filter((s) => s.status === 'completed').length;
  const totalSteps = steps.length;
  const completionPercentage =
    totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  return (
    <Card title="Onboarding Steps">
      <Card.Section>
        <Stack vertical gap={8}>
          <Stack style={{ justifyContent: 'space-between' }}>
            <Body weight="semibold">Progress</Body>
            <Body weight="semibold">
              {completedSteps} / {totalSteps}
            </Body>
          </Stack>
          <div
            style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#E5E7EB',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${completionPercentage}%`,
                height: '100%',
                backgroundColor: '#10B981',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <Small color="lighter">{completionPercentage}% Complete</Small>
        </Stack>
      </Card.Section>
      <Card.Section>
        <Stack vertical gap={12}>
          {steps.map((step) => (
            <Stack
              key={step.id}
              vertical
              gap={8}
              style={{
                padding: '12px',
                backgroundColor: step.disabled ? '#F3F4F6' : '#F9FAFB',
                borderRadius: '8px',
                borderLeft: `4px solid ${stepStatusColor[step.status]}`,
                opacity: step.disabled ? 0.6 : 1,
              }}
            >
              <Stack
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}
              >
                <Body weight="semibold">{step.title}</Body>
                <Select
                  disabled={step.disabled || updating === step.id}
                  size="small"
                  sx={{
                    minWidth: '140px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: stepStatusColor[step.status] + '20',
                    color: stepStatusColor[step.status],
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: stepStatusColor[step.status] + '40',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: stepStatusColor[step.status],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: stepStatusColor[step.status],
                    },
                  }}
                  value={step.status}
                  onChange={(e: SelectChangeEvent) =>
                    handleStatusChange(step.id, e.target.value)
                  }
                >
                  <MenuItem value="pending">
                    <span style={{ color: stepStatusColor.pending }}>
                      {stepStatusLabel.pending}
                    </span>
                  </MenuItem>
                  <MenuItem value="in_progress">
                    <span style={{ color: stepStatusColor.in_progress }}>
                      {stepStatusLabel.in_progress}
                    </span>
                  </MenuItem>
                  <MenuItem value="completed">
                    <span style={{ color: stepStatusColor.completed }}>
                      {stepStatusLabel.completed}
                    </span>
                  </MenuItem>
                  <MenuItem value="under_review">
                    <span style={{ color: stepStatusColor.under_review }}>
                      {stepStatusLabel.under_review}
                    </span>
                  </MenuItem>
                  <MenuItem value="rejected">
                    <span style={{ color: stepStatusColor.rejected }}>
                      {stepStatusLabel.rejected}
                    </span>
                  </MenuItem>
                </Select>
              </Stack>
              {step.disabled && (
                <Small color="lighter">
                  This step is locked until previous steps are completed
                </Small>
              )}
            </Stack>
          ))}
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default OnboardingStepsCard;
